import {
	createResource,
	type RouterDataContext,
	type RouteResource,
	useResource,
	type UseResourceHookResponse,
} from '@atlassian/react-resource-router';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';

const permissions = [
	'VIEW_BOARD',
	'ADMINISTER_BOARD',
	'BULK_CHANGE',
	'DELETE_ISSUE',
	'EDIT_ISSUE',
	'CREATE_ISSUE',
	'ASSIGN_ISSUE',
	'TRANSITION_ISSUE',
	'MANAGE_SPRINT',
	'SCHEDULE_ISSUE',
] as const;

type Permission = (typeof permissions)[number];

type PermissionResponse = {
	boardPermissions: Record<Permission, boolean>;
};

export const permissionsResource: RouteResource<PermissionResponse | null> =
	createResource<PermissionResponse | null>({
		type: 'BACKLOG_PERMISSIONS',
		getKey: ({ match }) => match.params.boardId || '',
		getData: async ({ match }: RouterDataContext) => {
			const boardId = match.params.boardId || '';
			return fetchJson<PermissionResponse | null>(
				`/rest/greenhopper/1.0/xboard/permissions?boardId=${boardId}&permissions=${permissions.join(
					',',
				)}`,
			).catch((error) => {
				log.safeErrorWithoutCustomerData(
					'backlog.load-permission.failure',
					'Failed to fetch permissions for Company-Managed backlog',
					error,
				);
				return null;
			});
		},
		// SSR doesn't care about this and we can't check FFs here because its not in the render path
		maxAge: 1000 * 60 * 60, // 1 hour max age
	});

export const usePermissionsResource = (): UseResourceHookResponse<PermissionResponse | null> =>
	useResource(permissionsResource);
